import { CO_CAU_HINH, layDb, layStorage } from './firebase.js';
import { PLAYBOOK_POSTS, LOCAL_SLUGS } from '../data/playbook.js';
import { canhBaoMatNoiDung, chuanHoaBaiViet, chuanHoaSlug, kiemTraBai } from './playbookModel.js';

export { chuanHoaBaiViet, chuanHoaSlug };

export async function docBaiDaXuatBan() {
  if (!CO_CAU_HINH) return PLAYBOOK_POSTS;
  try {
    const db = await layDb();
    const { collection, getDocs, query, where } = await import('firebase/firestore/lite');
    const snap = await getDocs(query(collection(db, 'articles'), where('status', '==', 'PUBLISHED')));
    const remote = snap.docs.map((doc) => chuanHoaBaiViet({ ...doc.data(), slug: doc.id }));
    const merged = new Map(PLAYBOOK_POSTS.map((post) => [post.slug, post]));
    remote.forEach((post) => merged.set(post.slug, post));
    return [...merged.values()].sort((a, b) => Number(b.featured) - Number(a.featured) || String(b.publishedAt).localeCompare(String(a.publishedAt)));
  } catch { return PLAYBOOK_POSTS; }
}

export async function docTatCaBai() {
  const db = await layDb();
  if (!db) throw new Error('Website chưa cấu hình Firestore.');
  const { collection, getDocs } = await import('firebase/firestore/lite');
  const snap = await getDocs(collection(db, 'articles'));
  // Chỉ lấy bài viết tay + bài đang có trên Firestore. Bản sao lúc build của bài
  // đã xoá không được hiện lại trong danh sách quản trị.
  const merged = new Map(PLAYBOOK_POSTS.filter((post) => LOCAL_SLUGS.has(post.slug)).map((post) => [post.slug, post]));
  snap.docs.forEach((item) => merged.set(item.id, chuanHoaBaiViet({ ...item.data(), slug: item.id })));
  return [...merged.values()];
}

export async function docBai(slug) {
  const local = PLAYBOOK_POSTS.find((post) => post.slug === slug);
  if (!CO_CAU_HINH) return local || null;
  try {
    const db = await layDb();
    const { doc, getDoc } = await import('firebase/firestore/lite');
    const snap = await getDoc(doc(db, 'articles', chuanHoaSlug(slug)));
    return snap.exists() ? chuanHoaBaiViet({ ...snap.data(), slug: snap.id }) : (local || null);
  } catch { return local || null; }
}

/** Xoá hẳn một bài trên Firestore. Firestore Rules chỉ cho tài khoản role admin. */
export async function xoaBai(slug) {
  const db = await layDb();
  if (!db) throw new Error('Website chưa cấu hình Firestore.');
  const { doc, deleteDoc } = await import('firebase/firestore/lite');
  await deleteDoc(doc(db, 'articles', chuanHoaSlug(slug)));
}

/**
 * Lưu bài. Trước khi ghi, đối chiếu với bản đang có trên Firestore: nếu bản sắp
 * lưu thiếu bảng, ảnh, phần hoặc mất quá nhiều chữ thì DỪNG LẠI và báo, vì đó
 * gần như luôn là dấu hiệu tab quản trị đang mở một bản cũ. Muốn ghi đè thật thì
 * gọi lại với { ghiDe: true }.
 */
export async function luuBai(raw, status, { ghiDe = false } = {}) {
  const post = chuanHoaBaiViet({ ...raw, status });
  const loi = kiemTraBai(post, status);
  if (loi.length) throw new Error(loi.join(' '));
  const db = await layDb();
  const { doc, getDoc, setDoc, serverTimestamp } = await import('firebase/firestore/lite');
  const ref = doc(db, 'articles', post.slug);
  if (!ghiDe) {
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const canh = canhBaoMatNoiDung(chuanHoaBaiViet({ ...snap.data(), slug: snap.id }), post);
      if (canh.length) {
        const err = new Error(`Bản đang sửa thiếu ${canh.join(', ')} so với bài trên website. Thường là do trang quản trị mở từ trước khi bài được cập nhật. Hãy tải lại bài rồi sửa; chỉ ghi đè nếu bạn thật sự muốn bỏ phần đó.`);
        err.canXacNhan = true;
        err.matNoiDung = canh;
        throw err;
      }
    }
  }
  // readingMinutes được tính lại mỗi lần đọc; vẫn lưu để công cụ khác dùng.
  await setDoc(ref, { ...post, updatedAt: serverTimestamp() }, { merge: true });
  return post;
}

/* ── Xử lý ảnh ngay trên trình duyệt trước khi tải lên ────────────────────
 * Ảnh chụp điện thoại thường 4–8 MB, rộng 4000px. Đưa thẳng lên bài sẽ làm
 * trang tải chậm và vỡ nhịp giao diện. Ở đây ảnh được thu về tối đa 1600px,
 * nén WebP, và ghi lại kích thước thật để trình duyệt giữ chỗ trước (không
 * nhảy bố cục khi ảnh tải xong).
 */
const MAX_INPUT = 15 * 1024 * 1024;

async function moAnh(file) {
  if (!file?.type?.startsWith('image/')) throw new Error('Tệp phải là hình ảnh (JPG, PNG, WebP).');
  if (file.size > MAX_INPUT) throw new Error('Ảnh gốc lớn hơn 15 MB, hãy chọn ảnh nhỏ hơn.');
  if ('createImageBitmap' in window) {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* rơi xuống cách dưới */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally { URL.revokeObjectURL(url); }
}

function veCanvas(src, sx, sy, sw, sh, dw, dh) {
  const canvas = document.createElement('canvas');
  canvas.width = dw; canvas.height = dh;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, dw, dh);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, sx, sy, sw, sh, 0, 0, dw, dh);
  return canvas;
}

function sangBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Không nén được ảnh.'))), type, quality));
}

async function taiLen(blob, slug, ext) {
  const storage = await layStorage();
  if (!storage) throw new Error('Website chưa cấu hình Firebase Storage.');
  const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
  const target = ref(storage, `playbook/${chuanHoaSlug(slug) || 'draft'}/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`);
  await uploadBytes(target, blob, { contentType: blob.type, cacheControl: 'public, max-age=31536000, immutable' });
  return getDownloadURL(target);
}

/** Ảnh trong bài: tối đa 1600px chiều rộng, WebP. Trả về { url, width, height }. */
export async function taiAnhPlaybook(file, slug) {
  const src = await moAnh(file);
  const w0 = src.width; const h0 = src.height;
  const scale = Math.min(1, 1600 / w0);
  const w = Math.round(w0 * scale); const h = Math.round(h0 * scale);
  const blob = await sangBlob(veCanvas(src, 0, 0, w0, h0, w, h), 'image/webp', 0.82);
  if (blob.size > 5 * 1024 * 1024) throw new Error('Ảnh sau khi nén vẫn lớn hơn 5 MB.');
  return { url: await taiLen(blob, slug, 'webp'), width: w, height: h };
}

/**
 * Ảnh chia sẻ mạng xã hội 1200×630, cắt giữa, JPEG (Zalo và một số ứng dụng
 * chưa hiển thị WebP trong thẻ xem trước).
 */
export async function taoAnhChiaSe(file, slug) {
  const src = await moAnh(file);
  const ratio = 1200 / 630;
  let sw = src.width; let sh = Math.round(sw / ratio);
  if (sh > src.height) { sh = src.height; sw = Math.round(sh * ratio); }
  const sx = Math.round((src.width - sw) / 2); const sy = Math.round((src.height - sh) / 2);
  const blob = await sangBlob(veCanvas(src, sx, sy, sw, sh, 1200, 630), 'image/jpeg', 0.86);
  return taiLen(blob, slug, 'jpg');
}
